import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { ModalService } from './modal.service';
import { AlertService } from './alert.service';
import { FileViewer } from '@capacitor/file-viewer';
import { StatusBarService } from './status-bar.service';
/** ICS js object*/
declare let ics: any;
/**
 * Add Calendar Service
 */
@Injectable({
	providedIn: 'root',
})
export class CalendarService {
	/**
	 * Calendar Service Constructor
	 * @param modalService - Modal Service for toaster notifications
	 * @param alertService - Alert Service for AlertController dialogs
	 * @param statusBarService - Status Bar Service for iOS status bar management
	 */
	constructor(
		private modalService: ModalService,
		private alertService: AlertService,
		private statusBarService: StatusBarService
	) {}
	/**
	 * Generating the calendar event
	 * @param id - Id for the event (generally Appointment Id)
	 * @param subject - Event subject
	 * @param description - Event Description
	 * @param location - Event Location
	 * @param begin - Event starting time
	 * @param end - Event End time
	 * @param filename - File name to be downloaded
	 */
	async addToCalendar(
		id: number,
		subject: string,
		description: string,
		location: string,
		begin: any,
		end: any,
		filename: string = 'Appointment'
	) {
		const cal = ics(id);
		cal.addEvent(subject, description, location, begin, end);

		/** Web */
		if (Capacitor.getPlatform() === 'web') {
			cal.download(filename);
			return;
		}

		/** Mobile: write ICS file to app's documents directory */
		const fileContent = cal.build();
		const safeFileName = filename.replace(/[^a-zA-Z0-9-_]/g, '_');
		const fileName = `${safeFileName}_${new Date().toISOString().replace(/[:.]/g, '-')}.ics`;

		try {
			await Filesystem.writeFile({
				path: fileName,
				data: fileContent,
				directory: Directory.Documents,
				encoding: Encoding.UTF8,
			});

			const uriResult = await Filesystem.getUri({
				path: fileName,
				directory: Directory.Documents,
			});

			await this.alertService.showTwoButtonAlert(
				'Download Success',
				'You can open the file to add it to your calendar now.',
				'Open',
				async () => {
					try {
						await FileViewer.openDocumentFromLocalPath({ path: uriResult.uri });
						this.statusBarService.setAppStatusBar();
					} catch (err) {
						console.error('Error opening file:', err);
						this.modalService.showToaster('Unable to open the calendar file.');
					}
				}
			);
		} catch (error) {
			this.modalService.showToaster('Failed to save calendar event. Please try again.');
		}
	}
}
